import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCBadge, RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCMicrosoftSocialLoginButton, RCCPopover, RCCRefMicrosoftSocialLoginButton, RCCRefModalProps, RCCSelectOptionsCard, RCCServicesApi, RCCSwitch, RCCTooltip, RCCUseMicrosoftSocialLogin } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import DeleteModal from "src/components/DeleteModal";
import { IRefComponentSelectOptionsCard } from "react-common-components/dist/components/SelectOptionsCard/interface";
import { IComponentDomainsModals, IRefDomainsModals, IEftba027 } from "./interface";
import { addAndChangeSchema, deleteSchema } from "./schemas";

const defaultConfigs = `{
	"clientId": "",
	"cloudInstance": "https://login.microsoftonline.com",
	"tenantInfo": ""
}`;

const ModalsDomains: React.ForwardRefRenderFunction<IRefDomainsModals, IComponentDomainsModals> = ({
	paginationRef
}, ref) => {
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const [selectedDomain, setSelectedDomain] = useState<IEftba027>({
		efa027Configuracao: defaultConfigs,
		efa027Tipo: 1,
		efa027Ativo: "S"
	} as IEftba027);
	const { REACT_APP_ADDRESS } = process.env;
	
	// Modal Delete
	const modalDelete = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit : handleSubmitDelete, 
		register : registerDelete, 
		reset : resetDelete, 
		setError : setErrorDelete, 
		formState: { errors : errorsDelete }, 
		clearErrors: clearErrorsDelete 
	} = useForm();
	const submitDelete = async (data: any) => {
		try{	
			clearErrorsDelete();
			modalDelete.current?.setLoadingButton();

			await deleteSchema(translate).validate({...data, "efa027DominioConfirmation": selectedDomain.efa027Dominio},{
				abortEarly: false
			});

			await RCCServicesApi.deleteRequest({
				method: "Domains/Delete",
				urlParameters: {
					id: selectedDomain.efa027Id
				}
			});

			addToast({
				color: "green",
				description: translate("Domínio excluído com sucesso"),
				title: translate("Sucesso")
			});
			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err){

			modalDelete.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorDelete))
				return;

			standardErrorMessages(err, translate("Erro ao excluir domínio"), addToast);
		}
		
	};
	const actionButtonDelete = (row: IEftba027) => {
		resetDelete(); 
		setSelectedDomain(row); 
		modalDelete.current?.openModal();
	};
	
	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const [isValid, setIsValid] = useState(false);
	const { updateMsalInstance } = RCCUseMicrosoftSocialLogin();
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit : handleSubmitAddOrChange, 
		control: controlAddOrChange,
		register : registerAddOrChange, 
		reset : resetAddOrChange, 
		setError : setErrorAddOrChange, 
		formState: { errors : errorsAddOrChange }, 
		clearErrors: clearErrorsAddOrChange,
		watch: watchAddOrChange,
		setValue: setValueAddOrChange
	} = useForm();
	const refSelectOptionsCard = useRef<IRefComponentSelectOptionsCard>(null);
	const refMicrosoftSocialLoginButton = useRef<RCCRefMicrosoftSocialLoginButton>(null);
	const onCallbackAddOrChange = useRef<Function | undefined>(undefined);
	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();
			
			await addAndChangeSchema(translate).validate({...data}, {
				abortEarly: false
			});


			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "Domains/Insert",
					body: {
						...data,
						efa027Ativo: data.efa027Ativo ? "S" : "N",
						efa027Obrigatorio: data.efa027Obrigatorio ? "S" : "N"
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "Domains/Update",
					body: {
						...data,
						efa027Id: selectedDomain.efa027Id,
						efa027Ativo: data.efa027Ativo ? "S" : "N",
						efa027Obrigatorio: data.efa027Obrigatorio ? "S" : "N"
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Domínio adicionado com sucesso") : translate("Domínio alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef.current?.reload();
		}catch(err){

			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange))
				return;

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar domínio") : translate("Erro ao alterar domínio"), addToast);
		}
	};
	const actionButtonAdd = (onCallback?: Function) => {
		resetAddOrChange(); 
		setIsValid(false);
		setSelectedDomain({
			efa027Configuracao: defaultConfigs,
			efa027Tipo: 1,
			efa027Ativo: "S" 
		} as IEftba027); 
		setIsAdd(true); 
		onCallbackAddOrChange.current = onCallback;
		modalAddOrChange.current?.openModal();
	};
	const actionButtonChange = (row: IEftba027) => {
		resetAddOrChange(); 
		setIsValid(true);
		setSelectedDomain(row); 
		setIsAdd(false); 
		onCallbackAddOrChange.current = undefined;
		modalAddOrChange.current?.openModal(); 
		setValueAddOrChange("efa027Configuracao", row.efa027Configuracao);
	};
	const validateMicrosoft = async () => {
		const clientId = watchAddOrChange("clientId");
		const cloudInstance = watchAddOrChange("cloudInstance");
		const tenantInfo = watchAddOrChange("tenantInfo");
		const domain = watchAddOrChange("efa027Dominio");
		if(!clientId || !cloudInstance || !tenantInfo || !domain){
			addToast({
				color: "yellow",
				description: translate("Preencha todos os campos para prosseguir com a validação"),
				title: translate("Atenção")
			});
			return;
		}
		await updateMsalInstance(clientId, cloudInstance, tenantInfo, REACT_APP_ADDRESS);

		try{
			const dt = await refMicrosoftSocialLoginButton?.current?.handleLogin();
				
			if(!dt || !dt.account)
				throw new Error();

			setIsValid(true);
			setValueAddOrChange("efa027Configuracao", JSON.stringify({
				clientId,
				cloudInstance,
				tenantInfo
			}));
		}catch(_){
			addToast({
				color: "red",
				description: translate("Erro ao efetuar login neste domínio"),
				title: translate("Erro")
			});
		}
	};
	const getInputsAccordingToType = (value: string) => {

		const defaultValueAsObj = JSON.parse(selectedDomain.efa027Configuracao);

		if(value === "1")
			return (
				<>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							register={registerAddOrChange}
							name="clientId"
							label={translate("Id do cliente")}
							maxLength={200}
							defaultValue={defaultValueAsObj.clientId}
							disabled={isValid}
							onChangeCapture={() => setIsValid(false)}
							required
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							register={registerAddOrChange}
							name="cloudInstance"
							label={translate("Instância nuvem da azure")}
							maxLength={200}
							defaultValue={defaultValueAsObj.cloudInstance}
							disabled={isValid}
							onChangeCapture={() => setIsValid(false)}
							required
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
						wrap="nowrap"
						alignItems="baseline"
					>
						<RCCGrid
							container
							style={{ marginRight: "4px" }}
						>
							<RCCInput 
								register={registerAddOrChange}
								name="tenantInfo"
								label="Tenant info"
								maxLength={200}
								defaultValue={defaultValueAsObj.tenantInfo}
								disabled={isValid}
								onChangeCapture={() => setIsValid(false)}
								required
							/>
						</RCCGrid>

						<RCCPopover
							header={{ title: "Tenant info" }}
							body={{ text: translate("Definir uma das seguintes opções: Se a sua candidatura suporta contas neste diretório organizacional, substitua este valor pelo ID do diretório (inquilino) ou nome do inquilino (por exemplo, contoso.microsoft.com). Se a sua candidatura suporta contas em qualquer diretório organizacional, substitua este valor por organizações. Se a sua aplicação suportar contas em qualquer diretório organizacional e contas pessoais da Microsoft, substitua este valor por comum. Para restringir apenas o suporte às contas pessoais da Microsoft, substitua este valor pelos consumidores.") }}
							position="leftEnd"
							styleInline="width: 22rem;"
						>
							<RCCIcon
								name="infoCircle"
							/>
						</RCCPopover>

					</RCCGrid>
					<br />
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
						alignItems="center"
						justifyContent="flex-end"
					>
						{!isValid ? (
							<RCCMicrosoftSocialLoginButton
								ref={refMicrosoftSocialLoginButton} 
								onClick={validateMicrosoft}
							>
								{translate("Validar domínio com a Microsoft")}
							</RCCMicrosoftSocialLoginButton>
						): (
							<RCCGrid
								container
								item
								alignItems="center"
								justifyContent="space-between"
							>
								<RCCTooltip
									color="grey"
									text={translate("Revalidar")}
								>
									<RCCButton 
										color="grey" 
										icon={{
											name: "refresh"
										}}
										layout="isRounded"
										size="sm"
										onClick={() => setIsValid(false)}
									/>
								</RCCTooltip>
								<RCCBadge color="green">
									{translate("Domínio validado com sucesso")}
								</RCCBadge>
							</RCCGrid>
						)}
					</RCCGrid>
				</>
			);

		return null;
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));
    
	return (
		<>
			{/* Modal Delete */}
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão do domínio")}
				description= {`${translate("Deseja realmente excluir o domínio")}: ${selectedDomain.efa027Dominio}?`}
				onSubmit = {handleSubmitDelete(submitDelete)} 
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="efa027Dominio"
						label={translate("Digite o nome do domínio para excluí-lo")}
						maxLength={200}
						error={errorsDelete.efa027Dominio}
						onChangeCapture={() => setIsValid(false)}
						required
					/>
				</RCCGrid>
			</DeleteModal>
			
			{/* Modal ADD/CHANGE */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar domínio") : translate("Alterar domínio"),
					icon: {
						name: "domain"
					}
				}}
				footer={{
					showSaveButton: isValid
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				side="right"
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCSwitch
							name="efa027Ativo"
							label={translate("Ativo")}
							register={registerAddOrChange}
							defaultChecked={selectedDomain.efa027Ativo === "S"}
							labelPosition="end"
						/>
						<RCCSwitch
							name="efa027Obrigatorio"
							label={translate("Obrigatório")}
							register={registerAddOrChange}
							defaultChecked={selectedDomain.efa027Obrigatorio === "S"}
							labelPosition="end"
							styleInline="margin-left: 8px;"
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							register={registerAddOrChange}
							name="efa027Dominio"
							label={translate("Domínio")}
							maxLength={200}
							defaultValue={selectedDomain.efa027Dominio}
							error={errorsAddOrChange.efa027Dominio}
							disabled={isValid}
							required
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCSelectOptionsCard 
							ref={refSelectOptionsCard}
							control={controlAddOrChange}
							name="efa027Tipo"
							label={translate("Tipo")}
							defaultValue={selectedDomain.efa027Tipo.toString()}
							options={[
								{
									value: "1",
									label: "Microsoft",
									icon: <RCCIcon name="microsoft" />
								}
							]}
						/>
					</RCCGrid>
					{getInputsAccordingToType(refSelectOptionsCard.current?.value ?? selectedDomain.efa027Tipo.toString())}
				</RCCGrid>
			</Modal>
		</>
	);
};

export default forwardRef(ModalsDomains);
